import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { App } from './App';
import PageSkeleton from './components/skeletons/PageSkeleton';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
// Lazy load pages
const Login = lazy(() => import('./pages/Login'));
const CRMDashboard = lazy(() => import('./pages/CRMDashboard'));
const MarketplaceOverview = lazy(() => import('./pages/MarketplaceOverview'));
const Users = lazy(() => import('./pages/Users'));
const UserCreate = lazy(() => import('./pages/UserCreate'));
const Partners = lazy(() => import('./pages/Partners'));
const PartnerCreate = lazy(() => import('./pages/PartnerCreate'));
const PartnerDetail = lazy(() => import('./pages/PartnerDetail'));
const Settings = lazy(() => import('./pages/Settings'));
const CRM = lazy(() => import('./pages/CRM'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const Community = lazy(() => import('./pages/Community'));
const Statistics = lazy(() => import('./pages/Statistics'));
// Marketplace section pages
const Rewards = lazy(() => import('./pages/Rewards'));
const RewardCreate = lazy(() => import('./pages/RewardCreate'));
const RewardDetail = lazy(() => import('./pages/RewardDetail'));
const Claims = lazy(() => import('./pages/Claims'));
// Statistics section pages
const ImpactIndices = lazy(() => import('./pages/ImpactIndices'));
const PlatformAnalytics = lazy(() => import('./pages/PlatformAnalytics'));
const AIBriefings = lazy(() => import('./pages/AIBriefings'));
// LMS section pages
const LMSOverview = lazy(() => import('./pages/lms/LMSOverview'));
const LMSModeration = lazy(() => import('./pages/lms/LMSModeration'));
const LMSProgramming = lazy(() => import('./pages/lms/LMSProgramming'));
const LMSCourses = lazy(() => import('./pages/lms/LMSCourses'));
// Old pages (keeping for reference)
const Insights = lazy(() => import('./pages/Insights'));
const Profile = lazy(() => import('./pages/Profile'));
const ProfilePreview = lazy(() => import('./pages/ProfilePreview'));
// Skeleton wrapper component
const SkeletonWrapper = ({
  type
}) => <div className="flex h-screen bg-gray-50" data-id="element-2">
    <div className="w-20 flex-shrink-0" data-id="element-3">
      <Sidebar data-id="element-4" />
    </div>
    <div className="flex-1 flex flex-col overflow-hidden" data-id="element-5">
      <Topbar data-id="element-6" />
      <div className="flex-1 overflow-y-auto" data-id="element-7">
        <PageSkeleton type={type} data-id="element-8" />
      </div>
    </div>
  </div>;
export function AppRouter() {
  useEffect(() => {
    document.title = 'Greenback Admin';
  }, []);
  return <BrowserRouter data-id="element-9">
      <Suspense fallback={<div className="min-h-screen bg-greenback flex items-center justify-center" data-id="element-11">
            <div className="text-white" data-id="element-12">Caricamento...</div>
          </div>} data-id="element-10">
        <Routes data-id="element-13">
          {/* Login route - default landing page */}
          <Route path="/login" element={<Login data-id="element-15" />} data-id="element-14" />

          {/* CRM Section - Main routes */}
          <Route path="/" element={<Navigate to="/dashboard" replace data-id="element-17" />} data-id="element-16" />
          <Route path="/dashboard" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-20" />} data-id="element-19">
                <CRMDashboard data-id="element-21" />
              </Suspense>} data-id="element-18" />
          <Route path="/users" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-24" />} data-id="element-23">
                <Users data-id="element-25" />
              </Suspense>} data-id="element-22" />
          <Route path="/users/new" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-28" />} data-id="element-27">
                <UserCreate data-id="element-29" />
              </Suspense>} data-id="element-26" />
          <Route path="/partners" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-32" />} data-id="element-31">
                <Partners data-id="element-33" />
              </Suspense>} data-id="element-30" />
          <Route path="/partners/:partnerId" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-36" />} data-id="element-35">
                <Partners data-id="element-37" />
              </Suspense>} data-id="element-34" />
          <Route path="/partners/new" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-40" />} data-id="element-39">
                <PartnerCreate data-id="element-41" />
              </Suspense>} data-id="element-38" />
          <Route path="/settings" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-44" />} data-id="element-43">
                <Settings data-id="element-45" />
              </Suspense>} data-id="element-42" />

          {/* Premi e offerte Section */}
          <Route path="/marketplace-overview" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-48" />} data-id="element-47">
                <MarketplaceOverview data-id="element-49" />
              </Suspense>} data-id="element-46" />
          <Route path="/rewards" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-52" />} data-id="element-51">
                <Rewards data-id="element-53" />
              </Suspense>} data-id="element-50" />
          <Route path="/rewards/new" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-56" />} data-id="element-55">
                <RewardCreate data-id="element-57" />
              </Suspense>} data-id="element-54" />
          <Route path="/rewards/:rewardId" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-60" />} data-id="element-59">
                <RewardDetail data-id="element-61" />
              </Suspense>} data-id="element-58" />
          <Route path="/claims" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-64" />} data-id="element-63">
                <Claims data-id="element-65" />
              </Suspense>} data-id="element-62" />

          {/* LMS & Community Section */}
          <Route path="/lms/overview" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-68" />} data-id="element-67">
                <LMSOverview data-id="element-69" />
              </Suspense>} data-id="element-66" />
          <Route path="/lms/moderazione" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-72" />} data-id="element-71">
                <LMSModeration data-id="element-73" />
              </Suspense>} data-id="element-70" />
          <Route path="/lms/programmazione" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-76" />} data-id="element-75">
                <LMSProgramming data-id="element-77" />
              </Suspense>} data-id="element-74" />
          <Route path="/lms/corsi" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-80" />} data-id="element-79">
                <LMSCourses data-id="element-81" />
              </Suspense>} data-id="element-78" />

          {/* Statistics Section */}
          <Route path="/impact-indices" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-84" />} data-id="element-83">
                <ImpactIndices data-id="element-85" />
              </Suspense>} data-id="element-82" />
          <Route path="/platform-analytics" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-88" />} data-id="element-87">
                <PlatformAnalytics data-id="element-89" />
              </Suspense>} data-id="element-86" />

          {/* Dropdown placeholder routes */}
          <Route path="/crm" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-92" />} data-id="element-91">
                <CRM data-id="element-93" />
              </Suspense>} data-id="element-90" />
          <Route path="/marketplace" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-96" />} data-id="element-95">
                <Marketplace data-id="element-97" />
              </Suspense>} data-id="element-94" />
          <Route path="/community" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-100" />} data-id="element-99">
                <Community data-id="element-101" />
              </Suspense>} data-id="element-98" />
          <Route path="/statistics" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-104" />} data-id="element-103">
                <Statistics data-id="element-105" />
              </Suspense>} data-id="element-102" />

          {/* Old routes (keeping for reference) */}
          <Route path="/overview" element={<App data-id="element-107" />} data-id="element-106" />
          <Route path="/insights" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-110" />} data-id="element-109">
                <Insights data-id="element-111" />
              </Suspense>} data-id="element-108" />
          <Route path="/profile" element={<Suspense fallback={<SkeletonWrapper type="dashboard" data-id="element-114" />} data-id="element-113">
                <Profile data-id="element-115" />
              </Suspense>} data-id="element-112" />
          <Route path="/profile/preview" element={<Suspense fallback={<div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-118">
                    <div className="text-gray-500" data-id="element-119">Caricamento...</div>
                  </div>} data-id="element-117">
                <ProfilePreview data-id="element-120" />
              </Suspense>} data-id="element-116" />

          {/* Catch all - redirect to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace data-id="element-122" />} data-id="element-121" />
        </Routes>
      </Suspense>
    </BrowserRouter>;
}